import React, { useState } from "react"

import { isBefore, startOfDay } from "date-fns"
import { Ban, Copy, Loader2 } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useAuth } from "@/contexts/AuthContext"
import { useCancelRouteInstance, useDuplicateRouteInstance } from "@/hooks/useCollections"
import { formatDayMonthYear } from "@/lib/dateFormats"
import { canCancelRoute, canWithdrawFromTrackpod } from "@/lib/statusDisplay"
import { Route } from "@/types/collections"

import AdhocCodeField from "./AdhocCodeField"

/**
 * Cancel and duplicate both require a reason, written as a route comment. Dialogs are exported for
 * the timeline row.
 */

const MAX_REASON = 500

const reasonHelp = (value: string, where: string) =>
  `Saved to ${where}. ${MAX_REASON - value.length} characters left.`

interface CancelRouteDialogProps {
  route: Route | null
  open: boolean
  onOpenChange: (open: boolean) => void
  viewDate?: Date
  onCancelled?: (route: Route) => void
}

export const CancelRouteDialog: React.FC<CancelRouteDialogProps> = ({
  route,
  open,
  onOpenChange,
  viewDate,
  onCancelled,
}) => {
  const { user } = useAuth()
  const cancelRoute = useCancelRouteInstance()
  const [reason, setReason] = useState("")

  const day = viewDate ?? route?.plannedStartTime

  /** Re-checked here: the route can change while the dialog is open. */
  const blockedReason = route ? canCancelRoute(route).reason : null

  /** Track-POD won't release a started route, so warn before confirming rather than after. */
  const withdrawsFromTrackpod = route ? canWithdrawFromTrackpod(route) : true

  const handleCancel = async () => {
    if (!route || !reason.trim() || blockedReason) return
    try {
      await cancelRoute.mutateAsync({
        routeInstanceId: route.id,
        routeCode: route.code,
        reason,
        authorName: user?.full_name || "Unknown User",
      })
      onOpenChange(false)
      setReason("")
      toast.success("Route cancelled", {
        description: withdrawsFromTrackpod
          ? `${route.code} is cancelled and withdrawn from the driver app. It stays on the day's record.`
          : `${route.code} is cancelled here and stays on the day's record. It had already started, so it remains in Track-POD — tell the driver directly.`,
      })
      onCancelled?.(route)
    } catch (error) {
      console.error("Error cancelling route:", error)
      toast.error("Could not cancel route", {
        description: "The route was not changed. Please try again.",
      })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setReason("")
        onOpenChange(next)
      }}
    >
      <DialogContent size="confirm">
        <DialogHeader>
          <DialogTitle>Cancel route {route?.code}?</DialogTitle>
          <DialogDescription>
            {withdrawsFromTrackpod
              ? "The route stops being dispatched and is withdrawn from the driver app. It stays"
              : "The route is marked cancelled here. It stays"}
            {day ? ` on ${formatDayMonthYear(day)}` : ""} as a cancelled route, with its stops
            and history intact, and can be shown again from the Status filter.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {blockedReason ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {blockedReason}.
            </p>
          ) : (
            <div className="grid gap-2">
              {!withdrawsFromTrackpod && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  This route has already started, so it cannot be pulled out of Track-POD. The
                  driver will still see it, and the stops they have already served keep their times
                  and PODs. Tell them directly that the route is off.
                </p>
              )}
              <Label htmlFor="cancel-reason">Reason for cancelling *</Label>
              <Textarea
                id="cancel-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON))}
                placeholder="e.g. Vehicle breakdown, no cover available"
                className="min-h-[80px] resize-none border-palette-jungleGreen200 bg-white text-sm"
              />
              <p className="text-xs text-palette-jungleGreen400">
                {reasonHelp(reason, "the route's activity")}
              </p>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={cancelRoute.isPending}
          >
            {blockedReason ? "Close" : "Keep route"}
          </Button>
          {!blockedReason && (
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={!reason.trim() || cancelRoute.isPending}
              className="gap-1.5"
            >
              {cancelRoute.isPending && <Loader2 size={14} className="animate-spin" />}
              {cancelRoute.isPending ? "Cancelling…" : "Cancel route"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface DuplicateRouteDialogProps {
  route: Route | null
  open: boolean
  onOpenChange: (open: boolean) => void
  dateStr: string
  onDuplicated?: (newRouteId: string) => void
}

export const DuplicateRouteDialog: React.FC<DuplicateRouteDialogProps> = ({
  route,
  open,
  onOpenChange,
  dateStr,
  onDuplicated,
}) => {
  const { user } = useAuth()
  const duplicateRoute = useDuplicateRouteInstance()
  const [reason, setReason] = useState("")
  const [customCode, setCustomCode] = useState("")

  const stopCount = route?.stops?.length ?? 0

  const handleDuplicate = async () => {
    if (!route || !reason.trim()) return
    try {
      const result = await duplicateRoute.mutateAsync({
        source: route,
        dateStr,
        reason,
        authorName: user?.full_name || "Unknown User",
        customCode,
      })
      onOpenChange(false)
      setReason("")
      setCustomCode("")
      toast.success("Route duplicated", {
        description: `Ad-hoc route created from ${result.sourceCode} with ${result.stopCount} ${
          result.stopCount === 1 ? "stop" : "stops"
        }.`,
      })
      onDuplicated?.(result.id)
    } catch (error) {
      console.error("Error duplicating route:", error)
      toast.error("Could not duplicate route", {
        description: "No route was created. Please try again.",
      })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setReason("")
          setCustomCode("")
        }
        onOpenChange(next)
      }}
    >
      <DialogContent size="confirm">
        <DialogHeader>
          <DialogTitle>Duplicate {route?.code} as ad-hoc</DialogTitle>
          <DialogDescription>
            Copies this route and its {stopCount} {stopCount === 1 ? "stop" : "stops"} to a new
            ad-hoc route on {formatDayMonthYear(new Date(`${dateStr}T00:00:00`))}, keeping the
            same times. The driver and vehicle are not copied.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-4">
          <AdhocCodeField id="duplicate-route-code" value={customCode} onChange={setCustomCode} />
          <div className="grid gap-2">
            <Label htmlFor="duplicate-reason">Reason for duplicating *</Label>
            <Textarea
              id="duplicate-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON))}
              placeholder="e.g. Overflow volume needs a second run"
              className="min-h-[80px] resize-none border-palette-jungleGreen200 bg-white text-sm"
            />
            <p className="text-xs text-palette-jungleGreen400">
              {reasonHelp(reason, "the new route's activity")}
            </p>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={duplicateRoute.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="confirm"
            onClick={handleDuplicate}
            disabled={!reason.trim() || duplicateRoute.isPending}
            className="gap-1.5"
          >
            {duplicateRoute.isPending && <Loader2 size={14} className="animate-spin" />}
            {duplicateRoute.isPending ? "Duplicating…" : "Duplicate route"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface RouteActionsProps {
  route: Route
  dateStr: string
  onCancelled?: (route: Route) => void
  onDuplicated?: (newRouteId: string) => void
}

export const RouteActions: React.FC<RouteActionsProps> = ({
  route,
  dateStr,
  onCancelled,
  onDuplicated,
}) => {
  const [cancelOpen, setCancelOpen] = useState(false)
  const [duplicateOpen, setDuplicateOpen] = useState(false)

  const viewDate = new Date(`${dateStr}T00:00:00`)
  const isPastDate = isBefore(startOfDay(viewDate), startOfDay(new Date()))

  // Disabled with a reason rather than hidden: a missing control reads as a bug.
  const cancelBlockedReason = canCancelRoute(route).reason

  const duplicateBlockedReason = isPastDate
    ? "Duplicates are created on the day you are viewing, which is in the past"
    : (route.stops?.length ?? 0) === 0
      ? "This route has no stops to copy"
      : null

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!!duplicateBlockedReason}
          title={duplicateBlockedReason ?? undefined}
          onClick={() => setDuplicateOpen(true)}
          className="h-7 gap-1.5 border-palette-jungleGreen200 text-xs text-palette-jungleGreen700 hover:bg-palette-jungleGreen100 hover:text-palette-jungleGreen900"
        >
          <Copy size={12} />
          Duplicate
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={!!cancelBlockedReason}
          title={cancelBlockedReason ?? undefined}
          onClick={() => setCancelOpen(true)}
          className="h-7 gap-1.5 border-palette-jungleGreen200 text-xs text-palette-jungleGreen700 hover:border-red-200 hover:bg-red-50 hover:text-red-700"
        >
          <Ban size={12} />
          Cancel route
        </Button>
      </div>

      <CancelRouteDialog
        route={route}
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        viewDate={viewDate}
        onCancelled={onCancelled}
      />
      <DuplicateRouteDialog
        route={route}
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
        dateStr={dateStr}
        onDuplicated={onDuplicated}
      />
    </>
  )
}

export default RouteActions
